/**
 * PWAInstallButton — Bouton "Installer l'app" + modale d'instructions.
 *
 * Utilisable dans menus, sidebars, ou pages.
 *  - Si déjà installée → bouton caché par défaut (prop `hideIfInstalled`)
 *  - Si Android/Chrome : clique → prompt natif (beforeinstallprompt)
 *  - Si iOS Safari : clique → modale avec instructions "Partager → Sur l'écran d'accueil"
 *  - Si autre navigateur sans prompt : modale avec instructions génériques
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Share, Smartphone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { usePWAInstall } from "@/lib/usePWAInstall";

export default function PWAInstallButton({
  variant = "default",
  size = "default",
  className = "",
  label = "Installer l'app",
  labelClassName = "",
  hideIfInstalled = true,
  testId = "pwa-install-button",
}) {
  const { canInstall, isStandalone, isIos, isInAppBrowser, promptInstall } = usePWAInstall();
  const [open, setOpen] = useState(false);

  if (isStandalone && hideIfInstalled) {
    return null;
  }

  async function handleClick() {
    if (canInstall) {
      const choice = await promptInstall();
      if (choice.outcome === "accepted") {
        toast.success("App installée ! Retrouvez-la sur votre écran d'accueil.");
      }
      return;
    }
    // Pas de prompt natif (iOS Safari OU navigateur non-PWA) → modale d'instructions
    setOpen(true);
  }

  return (
    <>
      <Button
        data-testid={testId}
        onClick={handleClick}
        variant={variant}
        size={size}
        className={className}
      >
        {isStandalone ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Download className="w-4 h-4 mr-2" />}
        <span className={labelClassName}>{isStandalone ? "App installée" : label}</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="pwa-install-modal">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-emerald-700" />
              </div>
              <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>
                Installer Véyèr CIEL
              </DialogTitle>
            </div>
            <DialogDescription className="text-xs leading-relaxed">
              Ajoutez l&apos;application sur votre écran d&apos;accueil pour un accès en
              un tap, l&apos;ouverture plein écran et un mode hors-ligne.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-2 text-xs">
            {isInAppBrowser ? (
              <div className="space-y-2 p-3 rounded-xl bg-amber-50/60 border border-amber-200">
                <p className="font-bold text-amber-900 text-sm">Ouvrez d&apos;abord la page dans votre navigateur</p>
                <p className="text-neutral-700 leading-relaxed">
                  L&apos;installation n&apos;est pas possible depuis une autre application (Facebook, Instagram…).
                  Utilisez son menu <strong>⋯</strong> puis <strong>« Ouvrir dans le navigateur »</strong>
                  {isIos ? " (ou « Ouvrir dans Safari »)" : ""}, puis appuyez de nouveau sur ce bouton.
                </p>
              </div>
            ) : isIos ? (
              <div className="space-y-2 p-3 rounded-xl bg-emerald-50/40 border border-emerald-100">
                <p className="font-bold text-emerald-900 text-sm">Sur iPhone / iPad :</p>
                <ol className="space-y-1.5 list-decimal list-inside text-neutral-700 leading-relaxed">
                  <li>
                    Appuyez sur l&apos;icône <Share className="w-3.5 h-3.5 inline -mt-0.5" /> <strong>Partager</strong>.
                    Si vous ne la voyez pas, elle se trouve dans le menu <strong>⋯</strong> à côté de la barre d&apos;adresse.
                  </li>
                  <li>Choisissez <strong>« Sur l&apos;écran d&apos;accueil »</strong> (faites défiler la liste si besoin)</li>
                  <li>Laissez <strong>« Ouvrir comme app web »</strong> activé s&apos;il est proposé, puis appuyez sur <strong>Ajouter</strong></li>
                </ol>
                <p className="text-[11px] text-neutral-500">
                  À la première ouverture de l&apos;app installée, reconnectez-vous : elle ne partage pas votre session avec le navigateur.
                </p>
              </div>
            ) : (
              <div className="space-y-2 p-3 rounded-xl bg-emerald-50/40 border border-emerald-100">
                <p className="font-bold text-emerald-900 text-sm">Sur Android / Chrome desktop :</p>
                <ol className="space-y-1.5 list-decimal list-inside text-neutral-700 leading-relaxed">
                  <li>Ouvrez le menu <strong>⋮</strong> (en haut à droite)</li>
                  <li>Choisissez <strong>« Installer l&apos;application »</strong> ou <strong>« Ajouter à l&apos;écran d&apos;accueil »</strong></li>
                  <li>Confirmez en cliquant sur <strong>Installer</strong></li>
                </ol>
                <p className="text-[11px] text-neutral-500 mt-2">
                  Si l&apos;option n&apos;apparaît pas, fermez et rouvrez l&apos;onglet — Chrome propose alors directement le bouton.
                </p>
              </div>
            )}

            <p className="text-[11px] text-neutral-500">
              L&apos;app reste accessible depuis votre navigateur normalement.
              L&apos;installation est juste un raccourci optimisé pour mobile.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
